import React from "react";
import { Form, TextArea } from "semantic-ui-react";
import { useCharacterCount } from "../../hooks/useCharacterCount";

/**
 * TextareaComponent renders a textarea field with validation.
 *
 * @typedef {object} InputProps
 * @property {string} name - Name of the input field.
 * @property {string} [label=''] - Label of the input field.
 * @property {string} placeholder - Placeholder text of the input field.
 * @property {string} [value=''] - Current value of the textarea.
 * @property {Function} onChange - Function called when textarea value changes.
 * @property {boolean} [labelAbove=false] - Boolean to determine if the label is above or next to the textarea.
 * @property {string} [labelStyle=''] - Style for the label.
 * @property {string} [fieldStyle=''] - Style for Form.Field.
 * @property {string} [textareaContainerStyle='w-100'] - Style for textarea container.
 * @property {number} [rows=5] - Number of rows the textarea has.
 *
 * @component
 * @param {InputProps} props - The component props.
 * @returns {JSX.Element} The rendered textarea input field with validation.
 */
export default function TextComponent({
  name,
  label = "",
  placeholder,
  value = "",
  onChange,
  labelAbove = false,
  labelStyle = "",
  fieldStyle = "",
  textareaContainerStyle = "w-100",
  rows = 5,
  maxLength = 750,
}) {
  const { characterCount, isNearLimit, isAtLimit } = useCharacterCount(
    value,
    maxLength,
  );

  return (
    <Form.Field className={`${!labelAbove && "flex-between"} ${fieldStyle}`}>
      {label && (
        <label className={`${!labelAbove && "w-30"} ${labelStyle}`}>
          <b>{label}</b>
        </label>
      )}
      <TextArea
        className={textareaContainerStyle}
        name={name}
        value={value}
        onChange={onChange}
        rows={rows}
        placeholder={placeholder}
      />
      <span
        style={{ color: isAtLimit ? "red" : isNearLimit ? "orange" : "gray" }}
      >
        {characterCount}/{maxLength}
      </span>
    </Form.Field>
  );
}
